import React, { useState } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import { Button, Form } from "react-bootstrap";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";

/** The API sends draw time as an ISO instant, the picker needs local "YYYY-MM-DDTHH:mm". */
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const CreateLottery = (props) => {
  const { onClose, editData, open, ...other } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [totalTickets, setTotalTickets] = useState("");
  const [maxNumber, setMaxNumber] = useState("30");
  const [ballCount, setBallCount] = useState("3");
  const [drawTime, setDrawTime] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [packageId, setPackageId] = useState("");
  const [packageList, setPackageList] = useState([]);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      fetchLotteryPackages();
      setErrors({});
      if (Object.keys(editData).length === 0) {
        setName("");
        setMultiplier("");
        setTotalTickets("");
        setMaxNumber("30");
        setBallCount("3");
        setDrawTime("");
        setIsActive(false);
        setPackageId("");
      } else {
        setName(editData?.name || "");
        setMultiplier(editData?.multiplier ?? "");
        setTotalTickets(editData?.totalTickets ?? "");
        setMaxNumber(editData?.maxNumber ?? "30");
        setBallCount(editData?.ballCount ? String(editData.ballCount) : "3");
        setDrawTime(toPickerValue(editData?.drawTime));
        setIsActive(editData?.isActive === 1);
        setPackageId(editData?.packageId || "");
      }
    }
  }, [editData, open]);

  const fetchLotteryPackages = () => {
    let url = `${endPoints.api.GET_LOTTERY_PACKAGES}`;
    getAllData(url)
      .then((response) => {
        if (response?.status === "success") {
          setPackageList(response?.data || []);
        }
      })
      .catch((err) => {
        console.error("Error fetching lottery packages:", err);
      });
  };

  const selectedPackage = packageList?.find((pkg) => String(pkg.id) === String(packageId));
  const previewPrize =
    selectedPackage && multiplier !== ""
      ? (parseFloat(selectedPackage.purchase_price) * parseFloat(multiplier)).toFixed(2)
      : null;

  const clearError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const next = {};

    if (!name.trim()) {
      next.name = "Lottery name is required";
    }

    const multiplierNum = parseFloat(multiplier);
    if (multiplier === "" || isNaN(multiplierNum) || multiplierNum <= 0) {
      next.multiplier = "Enter a multiplier greater than 0";
    }

    const totalTicketsNum = parseInt(totalTickets, 10);
    if (totalTickets === "" || isNaN(totalTicketsNum) || totalTicketsNum <= 0) {
      next.totalTickets = "Enter a whole number greater than 0";
    }

    const maxNumberNum = parseInt(maxNumber, 10);
    if (maxNumber === "" || isNaN(maxNumberNum) || maxNumberNum <= 0) {
      next.maxNumber = "Enter a whole number greater than 0";
    }

    const ballCountNum = parseInt(ballCount, 10);
    if (ballCount === "" || isNaN(ballCountNum) || ballCountNum <= 2) {
      next.ballCount = "Must be greater than 2";
    } else if (!isNaN(maxNumberNum) && maxNumberNum > 0 && ballCountNum > maxNumberNum) {
      next.ballCount = `Cannot exceed the ball range (1-${maxNumberNum})`;
    }

    if (!drawTime) {
      next.drawTime = "Draw time is required";
    } else if (new Date(drawTime).getTime() <= Date.now()) {
      next.drawTime = "Draw time must be in the future";
    }

    if (!packageId) {
      next.packageId = "Select a coin package for ticket purchases";
    }

    return next;
  };

  const handleCancel = () => {
    onClose();
  };

  const handleOk = () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      ErrorToast("Please fill the required fields");
      return;
    }

    let url = editData?.id
      ? `${endPoints.api.UPDATE_LOTTERY}`
      : `${endPoints.api.CREATE_LOTTERY}`;

    let payload = {
      name: name.trim(),
      multiplier: parseFloat(multiplier),
      totalTickets: parseInt(totalTickets, 10),
      maxNumber: parseInt(maxNumber, 10),
      ballCount: parseInt(ballCount, 10),
      drawTime: new Date(drawTime).toISOString(),
      packageId: parseInt(packageId, 10),
      isActive: isActive ? 1 : 0,
    };

    if (editData?.id) {
      payload.id = editData.id;
    }

    setIsLoading(true);
    addData(url, payload)
      .then((response) => {
        setIsLoading(false);
        if (response.data.status === "success") {
          SuccesToast(response?.data?.message);
          onClose();
        } else {
          ErrorToast(response?.data?.message);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast("Unable to save lottery. Backend not available yet.");
      });
  };

  return (
    <Dialog maxWidth="sm" open={open} {...other}>
      <DialogTitle>Lottery Info</DialogTitle>
      <DialogContent dividers>
        <Toaster></Toaster>
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Lottery Name"
            value={name}
            error={!!errors.name}
            helperText={errors.name}
            onChange={(event) => {
              setName(event.target.value);
              clearError("name");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Prize Multiplier (e.g. 10 = 10x ticket price)"
            type="number"
            value={multiplier}
            error={!!errors.multiplier}
            helperText={errors.multiplier}
            onChange={(event) => {
              setMultiplier(event.target.value);
              clearError("multiplier");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Total Tickets"
            type="number"
            value={totalTickets}
            error={!!errors.totalTickets}
            helperText={errors.totalTickets}
            onChange={(event) => {
              setTotalTickets(event.target.value);
              clearError("totalTickets");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Ball Number Range (1 to N)"
            type="number"
            value={maxNumber}
            error={!!errors.maxNumber}
            helperText={errors.maxNumber}
            onChange={(event) => {
              setMaxNumber(event.target.value);
              clearError("maxNumber");
              clearError("ballCount");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Numbers to Pick (must be greater than 2)"
            type="number"
            inputProps={{ min: 3 }}
            value={ballCount}
            error={!!errors.ballCount}
            helperText={errors.ballCount}
            onChange={(event) => {
              setBallCount(event.target.value);
              clearError("ballCount");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <label>Draw Time (America/New_York)</label>
          <TextField
            variant="outlined"
            type="datetime-local"
            value={drawTime}
            error={!!errors.drawTime}
            helperText={errors.drawTime}
            onChange={(event) => {
              setDrawTime(event.target.value);
              clearError("drawTime");
            }}
          />
        </FormControl>

        <Form.Group style={{ marginTop: "20px" }} controlId="lotteryPackageSelect">
          <Form.Label>Buy Ticket Package</Form.Label>
          <Form.Select
            value={packageId}
            isInvalid={!!errors.packageId}
            onChange={(event) => {
              setPackageId(event.target.value);
              clearError("packageId");
            }}
          >
            <option value="">-- Select Coin Package --</option>
            {packageList?.map((pkg) => (
              <option key={pkg.id} value={pkg.id}>
                {pkg.package_name} (${pkg.purchase_price})
              </option>
            ))}
          </Form.Select>
          {errors.packageId && (
            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
              {errors.packageId}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        {previewPrize && (
          <div style={{ marginTop: "12px", fontSize: "13px", color: "#03B349", fontWeight: 600 }}>
            Winner takes: ${selectedPackage.purchase_price} × {multiplier} = {previewPrize} SC
          </div>
        )}

        <FormControlLabel
          control={
            <Switch
              name="active"
              checked={isActive}
              onClick={() => setIsActive(!isActive)}
            />
          }
          labelPlacement="start"
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleOk}>Ok</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLottery;
